import React, { useEffect, useState } from 'react';
import { X, Save, Clock3, Mail, BellOff, Palette } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';

interface WorkflowSettingsPanelProps {
  onClose: () => void;
}

const STAGE_KEYS = [
  'draft',
  'submitted',
  'readyForScreening',
  'readyForProcessing',
  'immigration',
  'preDeparture',
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const WorkflowSettingsPanel: React.FC<WorkflowSettingsPanelProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await safeFetch('/api/admin/workflow/v2/settings');
        setSettings(data.settings || {});
        setPreview(data.nextRunPreview || null);
      } catch (e: any) {
        notifyToast('error', e.message || 'Failed to load workflow settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key: string, value: any) => setSettings((s: any) => ({ ...s, [key]: value }));
  const setStageFreq = (stage: string, value: string) => {
    const n = Number(value);
    setSettings((s: any) => ({
      ...s,
      nudgeFrequencyDaysByStage: { ...(s.nudgeFrequencyDaysByStage || {}), [stage]: value === '' ? '' : n },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    try {
      // Strip empty per-stage overrides so defaults apply.
      const freq: Record<string, number> = {};
      for (const [k, v] of Object.entries(settings.nudgeFrequencyDaysByStage || {})) {
        if (v !== '' && v !== null && v !== undefined) freq[k] = Number(v);
      }
      const data = await safeFetch('/api/admin/workflow/v2/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { ...settings, nudgeFrequencyDaysByStage: freq } }),
      });
      setSettings(data.settings || {});
      setPreview(data.nextRunPreview || null);
      notifyToast('success', 'Workflow settings saved');
    } catch (e: any) {
      const msg = e.message || 'Failed to save settings';
      notifyToast('error', msg);
      try {
        const parsed = JSON.parse(e.message || '{}');
        if (parsed.details) setErrors(parsed.details);
      } catch { /* message already shown */ }
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-blue-500';
  const labelCls = 'block text-xs font-bold text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 z-[9500] bg-black/50 backdrop-blur-xs flex justify-end animate-fade-in" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl p-6 space-y-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Workflow settings"
      >
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <h2 className="text-sm font-extrabold text-slate-900">Workflow Settings</h2>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl" aria-label="Close settings">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <p className="text-xs text-slate-500">Loading settings…</p>}

        {!loading && settings && (
          <>
            {errors.length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-800 space-y-1">
                {errors.map((e, i) => <p key={i}>• {e}</p>)}
              </div>
            )}

            {preview && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 space-y-1">
                <p className="font-bold flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" /> Next runs</p>
                <p>Nudge evaluation: {preview.nextNudgeRun ? new Date(preview.nextNudgeRun).toLocaleString() : '—'}</p>
                <p>Auto-advance: {preview.nextAutoAdvance ? new Date(preview.nextAutoAdvance).toLocaleString() : '—'}</p>
                <p>Recompute: {preview.nextRecompute ? new Date(preview.nextRecompute).toLocaleString() : '—'}</p>
              </div>
            )}

            <section className="space-y-3">
              <h3 className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5"><Mail className="h-4 w-4 text-blue-600" /> Nudge emails</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Default frequency (days)</label>
                  <input type="number" min={1} max={30} className={inputCls}
                    value={settings.defaultNudgeFrequencyDays ?? 3}
                    onChange={(e) => set('defaultNudgeFrequencyDays', Number(e.target.value))} />
                </div>
                <div>
                  <label className={labelCls}>Send time</label>
                  <input type="time" className={inputCls} value={settings.sendTime || '09:00'}
                    onChange={(e) => set('sendTime', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Timezone</label>
                <input className={inputCls} value={settings.timezone || 'America/Toronto'}
                  onChange={(e) => set('timezone', e.target.value)} placeholder="America/Toronto" />
              </div>
              <div>
                <label className={labelCls}>Per-stage frequency (days, blank = default)</label>
                <div className="grid grid-cols-2 gap-2">
                  {STAGE_KEYS.map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-slate-600 w-32 truncate" title={k}>{k}</span>
                      <input type="number" min={1} max={30} className={inputCls}
                        value={settings.nudgeFrequencyDaysByStage?.[k] ?? ''}
                        placeholder="default"
                        onChange={(e) => setStageFreq(k, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Quiet hours start</label>
                  <input type="time" className={inputCls} value={settings.quietHours?.start || '21:00'}
                    onChange={(e) => set('quietHours', { ...(settings.quietHours || {}), start: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Quiet hours end</label>
                  <input type="time" className={inputCls} value={settings.quietHours?.end || '08:00'}
                    onChange={(e) => set('quietHours', { ...(settings.quietHours || {}), end: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Quiet days</label>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d) => {
                    const active = (settings.quietDays || []).includes(d);
                    return (
                      <button key={d} type="button"
                        onClick={() => {
                          const cur: string[] = settings.quietDays || [];
                          set('quietDays', active ? cur.filter((x) => x !== d) : [...cur, d]);
                        }}
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border ${active ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200'}`}>
                        {d.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5"><BellOff className="h-4 w-4 text-amber-600" /> Automation</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Auto-advance run time</label>
                  <input type="time" className={inputCls} value={settings.autoAdvanceTime || '02:00'}
                    onChange={(e) => set('autoAdvanceTime', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Recompute interval (min)</label>
                  <input type="number" min={15} max={1440} className={inputCls}
                    value={settings.recomputeMinutes ?? 60}
                    onChange={(e) => set('recomputeMinutes', Number(e.target.value))} />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5"><Palette className="h-4 w-4 text-purple-600" /> Sender & brand</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Sender name</label>
                  <input className={inputCls} value={settings.senderName || ''}
                    onChange={(e) => set('senderName', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Reply-to</label>
                  <input className={inputCls} value={settings.replyTo || ''}
                    onChange={(e) => set('replyTo', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Brand name</label>
                <input className={inputCls} value={settings.brand?.name || ''}
                  onChange={(e) => set('brand', { ...(settings.brand || {}), name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Logo URL</label>
                <input className={inputCls} value={settings.brand?.logoUrl || ''}
                  onChange={(e) => set('brand', { ...(settings.brand || {}), logoUrl: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Primary color</label>
                  <input type="color" className="h-10 w-full rounded-xl border border-slate-200 cursor-pointer"
                    value={settings.brand?.primaryColor || '#1d4ed8'}
                    onChange={(e) => set('brand', { ...(settings.brand || {}), primaryColor: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Footer contact</label>
                  <input className={inputCls} value={settings.brand?.footerContact || ''}
                    onChange={(e) => set('brand', { ...(settings.brand || {}), footerContact: e.target.value })} />
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={onClose}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold rounded-xl flex items-center gap-1.5 disabled:opacity-50">
                <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
